<template>
  <div class="wpq-widget">
    <div v-if="loading" class="text-center py-4">
      <v-progress-circular indeterminate size="28" color="orange darken-1" />
    </div>
    <div v-else-if="fetchError" class="text-center py-4">
      <v-icon color="grey lighten-1" size="32">mdi-alert-circle-outline</v-icon>
      <p class="text-caption grey--text mt-2">Unable to load data</p>
    </div>
    <div v-else class="kpi-card-body">
      <div
        class="kpi-gradient-bg"
        :class="totalPending > 0 ? 'kpi-gradient-warn' : 'kpi-gradient-ok'"
      />

      <div class="d-flex align-center justify-space-between mb-3">
        <div class="kpi-icon-wrap" :class="totalPending > 0 ? 'kpi-icon-warn' : 'kpi-icon-ok'">
          <v-icon size="32" :color="toneColor(totalPending > 0 ? 'orange' : 'green')">
            {{ totalPending > 0 ? icons.mdiClockAlert : icons.mdiCheckCircle }}
          </v-icon>
        </div>
        <div class="text-right">
          <div class="kpi-number" :class="toneText(totalPending > 0 ? 'orange' : 'green')">
            {{ totalPending }}
          </div>
          <div class="text-caption grey--text font-weight-medium">Pending Imports</div>
        </div>
      </div>

      <div class="d-flex align-center justify-space-between mb-3">
        <span class="text-caption grey--text">Total Amount</span>
        <span class="text-body-2 font-weight-bold">{{ formatCurrency(totalAmount) }}</span>
      </div>

      <div v-if="batches.length > 0">
        <v-divider class="mb-2" />
        <div class="text-caption grey--text mb-1">Batches</div>
        <div
          v-for="batch in batches.slice(0, 5)"
          :key="batch.date"
          class="d-flex justify-space-between py-1 batch-row"
        >
          <span class="text-caption">{{ formatDate(batch.date) }}</span>
          <span class="text-caption font-weight-medium" :class="toneText('orange')">
            {{ batch.count }} &nbsp;·&nbsp; {{ formatCurrency(batch.amount) }}
          </span>
        </div>
      </div>

      <v-divider class="my-3" />

      <div class="d-flex align-center justify-space-between">
        <span
          class="text-caption font-weight-medium"
          :class="toneText(totalPending > 0 ? 'orange' : 'green')"
        >
          {{ totalPending > 0 ? 'Action needed' : 'Queue empty' }}
        </span>
        <v-btn
          v-if="totalPending > 0"
          x-small text rounded
          color="orange darken-1"
          @click="goToPayments"
        >
          View
          <v-icon x-small right>{{ icons.mdiArrowRight }}</v-icon>
        </v-btn>

      </div>
    </div>
  </div>
</template>

<script>
import { mdiClockAlertOutline, mdiCheckCircleOutline, mdiArrowRight } from '@mdi/js';
import { clients } from '../../../util/clients';
import WidgetThemeMixin from '../WidgetTheme.mixin';

const { backendRest } = clients.direct;

export default {
  name: 'WebPaymentQueueWidget',
  mixins: [WidgetThemeMixin],
  data: () => ({
    loading: true,
    fetchError: false,
    totalPending: 0,
    totalAmount: 0,
    batches: [],
    icons: {
      mdiClockAlert: mdiClockAlertOutline,
      mdiCheckCircle: mdiCheckCircleOutline,
      mdiArrowRight,
    },
  }),
  async created() {
    await this.fetchData();
  },
  methods: {
    goToPayments() {
      this.$router.push('/tax/payments/CcPayments');
    },
    async fetchData() {
      this.loading = true;
      try {
        const response = await backendRest.get('/dashboard/web-payment-queue');
        const data = response.data || {};
        this.totalPending = data.totalPending || 0;
        this.totalAmount = data.totalAmount || 0;
        this.batches = data.batches || [];
      } catch (e) {
        console.error('WebPaymentQueueWidget fetch failed', e);
        this.fetchError = true;
        this.totalPending = 0;
        this.totalAmount = 0;
        this.batches = [];
      } finally {
        this.loading = false;
      }
    },
    formatCurrency(val) {
      const n = val || 0;
      return `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    },
    formatDate(dateStr) {
      if (!dateStr) return '—';
      const d = new Date(dateStr);
      if (!Number.isFinite(d.getTime())) return dateStr;
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    },

    async refresh() {
      await this.fetchData();
    },
  },
};
</script>

<style scoped>
.wpq-widget { position: relative; }
.batch-row { border-bottom: 1px solid var(--widget-hairline, #f5f5f5); }
.kpi-card-body { padding: 4px 0; position: relative; }
.kpi-gradient-bg {
  position: absolute; top: -20px; right: -20px;
  width: 120px; height: 120px; border-radius: 50%;
  opacity: 0.18; pointer-events: none;
}
.kpi-gradient-warn { background: radial-gradient(circle, #ff9800 0%, transparent 70%); }
.kpi-gradient-ok { background: radial-gradient(circle, #43a047 0%, transparent 70%); }
.kpi-icon-wrap {
  width: 64px; height: 64px; border-radius: 16px;
  display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.kpi-icon-warn {
  background: var(--widget-tint-warn, linear-gradient(135deg, #fff8e1, #ffe0b2));
  box-shadow: 0 4px 16px rgba(255, 152, 0, 0.25);
}
.kpi-icon-ok {
  background: var(--widget-tint-ok, linear-gradient(135deg, #e8f5e9, #c8e6c9));
  box-shadow: 0 4px 16px rgba(67, 160, 71, 0.2);
}
.kpi-number {
  font-size: 2.8rem; font-weight: 800;
  line-height: 1; letter-spacing: -2px;
}
.kpi-action-btn {
  font-size: 11px !important; text-transform: none !important;
  letter-spacing: 0 !important; height: 26px !important;
}
</style>
